import React, { useEffect, useId, useRef, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, hasWelcomeDialogPending, clearWelcomeDialogPending } from '../contexts/AuthContext';
import { organizationsService, SELECTED_ORGANIZATION_EVENT } from '../services/organizationsService';
import { UploadPollingProvider } from '../contexts/UploadPollingContext';
import { DownloadPollingProvider } from '../contexts/DownloadPollingContext';
import {
  LayoutDashboard,
  Braces,
  Settings,
  Menu,
  X,
  LogOut,
  Upload,
  Download,
  Search,
  MessageCircle,
  Activity,
  BarChart3,
  Layers,
  BookText,
  FileBarChart,
  User,
  Tags,
} from 'lucide-react';
import VersionUpdater from './VersionUpdater';
import WelcomeDialog from './ui/WelcomeDialog';
import { authService } from '../services/authService';

/**
 * Cube glyph from the brand logo (`public/cube-logo.svg`), wordmark omitted.
 * Gradient ids are per-instance: the mobile sidebar renders a second copy inside a
 * `display:none` subtree, and shared ids would resolve there and paint nothing.
 */
function CubeMark({ className = 'h-5 w-5' }) {
  const uid = useId().replace(/:/g, '');
  const topId = `cubeMarkTop-${uid}`;
  const spineId = `cubeMarkSpine-${uid}`;
  const bottomId = `cubeMarkBottom-${uid}`;
  return (
    <svg className={className} viewBox="0 0 32 38" fill="none" aria-hidden="true">
      <path
        d="M2.03546 29.4242C1.76613 29.4242 1.49947 29.3707 1.25094 29.2669C1.00241 29.1632 0.776957 29.0111 0.58764 28.8195C0.398323 28.6279 0.248911 28.4007 0.148063 28.1509C0.0472156 27.9012 -0.0030621 27.6339 0.000144243 27.3646V11.3971C-0.00137616 11.0427 0.0903692 10.6943 0.266164 10.3866C0.44196 10.079 0.695615 9.823 1.00165 9.64443L14.942 1.43854C15.2546 1.25219 15.6118 1.15381 15.9758 1.15381C16.3397 1.15381 16.6969 1.25219 17.0096 1.43854L30.9499 9.64443C31.3863 9.93114 31.6959 10.3746 31.8145 10.8832C31.9331 11.3917 31.8517 11.9264 31.5872 12.3766C31.3227 12.8268 30.8952 13.1581 30.3933 13.302C29.8913 13.4459 29.3532 13.3914 28.8904 13.1497L15.9677 5.54956L4.03847 12.5601V27.3646C4.04495 27.903 3.8378 28.4221 3.46238 28.8081C3.08696 29.1941 2.57386 29.4157 2.03546 29.4242Z"
        fill={`url(#${topId})`}
      />
      <path
        d="M15.9757 21.6301C15.6118 21.6319 15.2543 21.5342 14.9419 21.3474L1.00161 13.1496C0.565181 12.8629 0.255642 12.4194 0.137019 11.9109C0.0183957 11.4024 0.0997645 10.8677 0.364295 10.4175C0.628825 9.96724 1.05628 9.63592 1.55824 9.49202C2.06021 9.34812 2.59828 9.40265 3.06116 9.64434L17.0095 17.8422C17.3938 18.069 17.6929 18.4159 17.8607 18.8294C18.0285 19.243 18.0558 19.7002 17.9383 20.1307C17.8208 20.5612 17.565 20.9411 17.2104 21.2121C16.8558 21.483 16.422 21.6299 15.9757 21.6301Z"
        fill={`url(#${spineId})`}
      />
      <path
        d="M15.9758 37.6056C15.6124 37.6035 15.2559 37.506 14.942 37.3229L7.42266 32.8969C7.03233 32.5924 6.76638 32.1561 6.67458 31.6696C6.58279 31.1831 6.67145 30.6798 6.92397 30.254C7.17649 29.8282 7.57555 29.509 8.04645 29.3562C8.51735 29.2034 9.02779 29.2275 9.48221 29.424L15.9435 33.2442L27.8808 26.1933V18.9243C27.9431 18.4328 28.1826 17.9808 28.5543 17.6532C28.9261 17.3256 29.4045 17.1449 29.9 17.1449C30.3955 17.1449 30.8739 17.3256 31.2457 17.6532C31.6174 17.9808 31.8569 18.4328 31.9192 18.9243V27.4048C31.9207 27.7591 31.8289 28.1076 31.6531 28.4152C31.4774 28.7229 31.2237 28.9788 30.9177 29.1574L17.0097 37.3229C16.6957 37.506 16.3393 37.6035 15.9758 37.6056Z"
        fill={`url(#${bottomId})`}
      />
      <defs>
        <linearGradient id={topId} x1="32.7348" y1="39.0112" x2="-0.710606" y2="-0.160636" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0063FC" />
          <stop offset="1" stopColor="#05DBF9" />
        </linearGradient>
        <linearGradient id={spineId} x1="30.6268" y1="40.8122" x2="-2.81864" y2="1.64845" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0063FC" />
          <stop offset="1" stopColor="#05DBF9" />
        </linearGradient>
        <linearGradient id={bottomId} x1="31.8142" y1="39.8025" x2="-1.63127" y2="0.630667" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0063FC" />
          <stop offset="1" stopColor="#05DBF9" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function getInitials(user) {
  if (user?.first_name && user?.last_name) {
    return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
  }
  if (user?.first_name) return user.first_name.slice(0, 2).toUpperCase();
  if (user?.email) {
    const local = user.email.split('@')[0];
    const parts = local.split(/[._-]/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return local.slice(0, 2).toUpperCase();
  }
  return 'U';
}

function BrandBlock() {
  const [workspace, setWorkspace] = useState(() => organizationsService.getSelectedOrganization('name') || '');

  useEffect(() => {
    const syncWorkspace = () => setWorkspace(organizationsService.getSelectedOrganization('name') || '');
    syncWorkspace();
    window.addEventListener(SELECTED_ORGANIZATION_EVENT, syncWorkspace);
    return () => window.removeEventListener(SELECTED_ORGANIZATION_EVENT, syncWorkspace);
  }, []);

  return (
    <div className="flex items-center gap-3 px-2">
      <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center">
        <CubeMark className="h-7 w-7" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] leading-none text-zinc-400">The Cube</p>
        <p className="mt-1 truncate text-[15px] font-semibold leading-none text-[#18181B]" title={workspace}>
          {workspace}
        </p>
      </div>
    </div>
  );
}

function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [hasFastCubeAccess, setHasFastCubeAccess] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef(null);
  const { user, logout, passwordResetRequired, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!passwordResetRequired && user?.show_welcome !== false && hasWelcomeDialogPending()) {
      setShowWelcomeDialog(true);
    }
  }, [passwordResetRequired, user?.show_welcome]);

  useEffect(() => {
    let cancelled = false;
    const loadOrganizations = async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (!cancelled) {
          setHasFastCubeAccess(organizationsService.hasFastCubeAccess(orgs));
        }
      } catch {
        if (!cancelled) {
          setHasFastCubeAccess(false);
        }
      }
    };
    loadOrganizations();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onDocClick = (event) => {
      if (avatarRef.current && !avatarRef.current.contains(event.target)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const navigationGroups = [
    {
      label: 'Reports',
      items: [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
        { name: 'Offer Reports', href: '/offer-report', icon: FileBarChart },
        { name: 'Upload', href: '/file-upload', icon: Upload },
        { name: 'Segments', href: '/segments', icon: Layers },
        ...(hasFastCubeAccess ? [{ name: 'Chat', href: '/chat', icon: MessageCircle }] : []),
      ],
    },
    {
      label: 'Data',
      items: [
        { name: 'Query', href: '/query', icon: Search },
        { name: 'Email List Download', href: '/file-download', icon: Download },
        { name: 'Signal File Request', href: '/signals', icon: Activity },
      ],
    },
    {
      label: 'Tools',
      items: [
        { name: 'API', href: '/api-keys', icon: Braces },
        { name: 'API Usage', href: '/api-usage', icon: BarChart3 },
        { name: 'Org Properties', href: '/organization-properties', icon: Tags },
      ],
    },
    {
      label: 'Admin-Only',
      items: [
        ...(user?.is_admin && hasFastCubeAccess
          ? [{ name: 'Data Dictionary', href: '/data-dictionary', icon: BookText }]
          : []),
        ...(user?.is_admin ? [{ name: 'Admin', href: '/admin', icon: Settings }] : []),
      ],
    },
  ].filter((group) => group.items.length > 0);
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleWelcomeClose = () => {
    clearWelcomeDialogPending();
    setShowWelcomeDialog(false);
  };

  const handleWelcomeConfirm = async (displayWelcome) => {
    clearWelcomeDialogPending();
    setShowWelcomeDialog(false);
    if (displayWelcome) {
      return;
    }
    try {
      await authService.updateShowWelcome(false);
      if (setUser && user) {
        setUser({ ...user, show_welcome: false });
      }
    } catch {
      /* keep showing next login if persist fails */
    }
  };

  const navLinkClass = (isActive, isDisabled) =>
    `group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
      isActive
        ? 'bg-[#E8EEF5] text-[#18181B]'
        : isDisabled
        ? 'text-zinc-400 cursor-not-allowed opacity-50'
        : 'text-zinc-500 hover:bg-[#E8EEF5] hover:text-[#18181B]'
    }`;

  const renderNav = (onNavigate) =>
    navigationGroups.map((group) => (
      <div key={group.label}>
        <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">{group.label}</p>
        <div className="space-y-0.5">
          {group.items.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href === '/segments' && location.pathname.startsWith('/segments/'));
            const isDisabled = passwordResetRequired && item.href !== '/profile';
            return (
              <Link
                key={item.name}
                to={isDisabled ? '/profile' : item.href}
                onClick={(e) => {
                  if (isDisabled) {
                    e.preventDefault();
                    navigate('/profile');
                  }
                  onNavigate?.();
                }}
                className={navLinkClass(isActive, isDisabled)}
              >
                <item.icon
                  className={`mr-3 h-[18px] w-[18px] ${
                    isActive ? 'text-[#18181B]' : 'text-zinc-400 group-hover:text-zinc-500'
                  }`}
                  strokeWidth={1.75}
                />
                {item.name}
              </Link>
            );
          })}
        </div>
      </div>
    ));

  return (
    <UploadPollingProvider>
    <DownloadPollingProvider>
    <div className="h-screen flex overflow-hidden bg-canvas">
      <VersionUpdater />
      <WelcomeDialog
        isOpen={showWelcomeDialog}
        onClose={handleWelcomeClose}
        onConfirm={handleWelcomeConfirm}
      />
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-sidebar">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 px-4">
              <BrandBlock />
            </div>
            <nav className="mt-6 px-3 space-y-5">{renderNav(() => setSidebarOpen(false))}</nav>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-[260px]">
          <div className="flex flex-col h-0 flex-1 border-r border-hairline bg-sidebar">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <div className="flex-shrink-0 px-4 pb-4">
                <BrandBlock />
              </div>
              <nav className="mt-2 flex-1 px-3 bg-sidebar space-y-6">{renderNav()}</nav>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-surface border-b border-hairline">
          <button
            type="button"
            className="px-4 border-r border-hairline text-zinc-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#3B82F6] md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 sm:px-6 flex items-center justify-end">
            <div className="relative" ref={avatarRef}>
              <button
                type="button"
                onClick={() => setAvatarOpen((open) => !open)}
                className="h-8 w-8 rounded-full bg-[#18181B] text-white inline-flex items-center justify-center text-[11px] font-semibold tracking-wide"
                aria-label="Account menu"
                aria-expanded={avatarOpen}
              >
                {getInitials(user)}
              </button>
              {avatarOpen && (
                <div className="absolute right-0 mt-1.5 w-44 rounded-lg border border-hairline bg-surface p-1 shadow-card">
                  <Link
                    to="/profile"
                    onClick={() => setAvatarOpen(false)}
                    className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-[#3F3F46] hover:bg-[#E8EEF5]"
                  >
                    <User className="h-4 w-4 text-zinc-400" />
                    Profile
                  </Link>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-[#3F3F46] hover:bg-[#E8EEF5]"
                  >
                    <LogOut className="h-4 w-4 text-zinc-400" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-8">
            <div
              className={`mx-auto px-4 sm:px-6 md:px-8 ${
                location.pathname.startsWith('/segments')
                  ? 'max-w-none'
                  : location.pathname.startsWith('/admin')
                    ? 'max-w-[100rem]'
                    : 'max-w-[1500px]'
              }`}
            >
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
    </DownloadPollingProvider>
    </UploadPollingProvider>
  );
}

export default Layout;
